import { ProjectGraphProjectNode } from '@nx/devkit';

/**
 * Make sure this matches the DATIQ_CI_SEPERATOR in the datiq-app.models.ts file
 *
 * We can't import it due to this being a plugin and can't access external libraries
 */
export const DATIQ_TAG_SPERATOR = ':';

export type LibraryTypes =
    | 'Feature'
    | 'Data'
    | 'Utils'
    | 'UI'
    | 'Core'
    | 'DesignSystem'
    | 'Unknown';
export type EnvironmentKeys =
    | 'nprod'
    | 'staging'
    | 'prod'
    | 'notUsed'
    | 'Unknown';
export enum Prefixes {
    Type = 'type',
    Team = 'team',
    Proj = 'proj',
    Domain = 'domain',
    Environment = 'env',
    Critical = 'critical',
}

export type Teams =
    | 'RedBarclays'
    | 'PorkChopExpress'
    | 'BinaryBrains'
    | 'FrontEndSquad'
    | 'Unknown'
    | 'Elevation'
    | 'CrossTeam'
    | 'WorksOnMyMachine';

export type Components =
    | 'BenchmarkAnalytics'
    | 'RateLane'
    | 'Trendlines'
    | 'NX'
    | 'Builder'
    | 'Trendlines'
    | 'MarketConditions'
    | 'Landing'
    | 'RateNational'
    | 'Job'
    | 'RateLaneBatch'
    | 'Unknown'
    | 'Social'
    | 'Core'
    | 'RFP'
    | 'SharedComponents'
    | 'CI_CD';

export type ProjectUpdateInfo = {
    type: LibraryTypes;
    team: Teams;
    proj: string;
    domain: Components;
    maxEnv: EnvironmentKeys;
    critical: boolean;
};

export type ProjectInfo = {
    key: string;
    type: string;
    team: Teams;
    proj: string;
    domain: Components;
    nprod: boolean;
    staging: boolean;
    prod: boolean;
    notUsed: boolean;
    critical: boolean;
    hasTags: boolean;
    hasNonCriticalDeps: boolean;
    node?: ProjectGraphProjectNode;
    deps?: Record<string, Partial<ProjectInfo>>;
};

export const emptyProjectInfo: ProjectInfo = {
    key: '',
    type: 'Unknown',
    team: 'Unknown',
    proj: '',
    domain: 'Unknown',
    nprod: false,
    staging: false,
    prod: false,
    notUsed: false,
    critical: false,
    hasTags: false,
    hasNonCriticalDeps: false,
};

export type DatiqNxRepoStatisProjectMapRes = {
    projects: Record<string, ProjectInfo>;
};

export type DatiqNxRepoStatusProjectRes = {
    project: ProjectInfo;
};
export type DatiqNxRepoStatusProjectSummaryRes = {
    summary: ProjectSummary;
};
export type DatiqNxRepoStatusProjectKeysRes = {
    keys: string[];
};

export type TeamSummary = {
    total: number;
    percentage: number;
};
export type DepSummary = {
    total: number;
    nonCritical: number;
    critical: number;
    criticalPercentage: number;
    nonCriticalPercentage: number;
};
export type DomainSummary = {
    total: number;
    percentage: number;
};
export type LibrarySummary = {
    total: number;
    percentage: number;
};
export type EnvSummary = {
    total: number;
    percentage: number;
};

export type ProjectSummary = {
    projects: number;
    teams: Record<Teams, TeamSummary>;
    deps: DepSummary;
    env: Record<EnvironmentKeys, EnvSummary>;
    domains: Record<Components, DomainSummary>;
    libraryTypes: Record<LibraryTypes, LibrarySummary>;
};

export const emptyProjectSummary: ProjectSummary = {
    projects: 0,
    env: {
        nprod: {
            total: 0,
            percentage: 0,
        },
        staging: {
            total: 0,
            percentage: 0,
        },
        prod: {
            total: 0,
            percentage: 0,
        },
        notUsed: {
            total: 0,
            percentage: 0,
        },
        Unknown: {
            total: 0,
            percentage: 0,
        },
    },
    teams: {
        RedBarclays: {
            total: 0,
            percentage: 0,
        },
        PorkChopExpress: {
            total: 0,
            percentage: 0,
        },
        BinaryBrains: {
            total: 0,
            percentage: 0,
        },
        FrontEndSquad: {
            total: 0,
            percentage: 0,
        },
        Unknown: {
            total: 0,
            percentage: 0,
        },
        Elevation: {
            total: 0,
            percentage: 0,
        },
        CrossTeam: {
            total: 0,
            percentage: 0,
        },
        WorksOnMyMachine: {
            total: 0,
            percentage: 0,
        },
    },
    deps: {
        total: 0,
        nonCritical: 0,
        critical: 0,
        criticalPercentage: 0,
        nonCriticalPercentage: 0,
    },
    libraryTypes: {
        Feature: {
            total: 0,
            percentage: 0,
        },
        Data: {
            total: 0,
            percentage: 0,
        },
        Utils: {
            total: 0,
            percentage: 0,
        },
        UI: {
            total: 0,
            percentage: 0,
        },
        Core: {
            total: 0,
            percentage: 0,
        },
        DesignSystem: {
            total: 0,
            percentage: 0,
        },
        Unknown: {
            total: 0,
            percentage: 0,
        },
    },
    domains: {
        Unknown: {
            total: 0,
            percentage: 0,
        },
        BenchmarkAnalytics: {
            total: 0,
            percentage: 0,
        },
        RateLane: {
            total: 0,
            percentage: 0,
        },
        Trendlines: {
            total: 0,
            percentage: 0,
        },
        NX: {
            total: 0,
            percentage: 0,
        },
        Builder: {
            total: 0,
            percentage: 0,
        },
        MarketConditions: {
            total: 0,
            percentage: 0,
        },
        Landing: {
            total: 0,
            percentage: 0,
        },
        RateNational: {
            total: 0,
            percentage: 0,
        },
        Job: {
            total: 0,
            percentage: 0,
        },
        RateLaneBatch: {
            total: 0,
            percentage: 0,
        },
        Social: {
            total: 0,
            percentage: 0,
        },
        Core: {
            total: 0,
            percentage: 0,
        },
        RFP: {
            total: 0,
            percentage: 0,
        },
        SharedComponents: {
            total: 0,
            percentage: 0,
        },
        CI_CD: {
            total: 0,
            percentage: 0,
        },
    },
};

export type ProjectQuery = {
    keys: string[];
    teams: Teams[];
    domains: Components[];
    types: LibraryTypes[];
    envs: EnvironmentKeys[];
    affected?: boolean;
    critical?: boolean;
};

export const emptyProjectQuery: ProjectQuery = {
    keys: [],
    teams: [],
    domains: [],
    types: [],
    envs: [],
};

export interface INxStatusProjectService {
    getProjects(): Promise<Record<string, ProjectInfo>>;
    getProject(project: string): Promise<ProjectInfo>;
    queryProjects(query: ProjectQuery): Promise<ProjectInfo[]>;
    getProjectSummary(query: ProjectQuery): Promise<ProjectSummary>;
    queryProjectKeys(query: ProjectQuery): Promise<string[]>;
    exportProject(fileType: string, query: ProjectQuery): Promise<string>;
    updateProject(
        projectKey: string,
        projectInfo: Partial<ProjectUpdateInfo>,
    ): Promise<ProjectInfo>;
}
